import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type {
  CobranzaDocumento,
  CobranzaPaymentLineSnapshot,
  CobranzaInstallmentState,
  CuentaPorCobrarSummary,
  CreditoPagadoResumen,
  RegistrarCobranzaInput,
  RegistrarCobranzaMultipleInput,
} from '@/modelos/cobranzas/cobranzas.types';
import type { Currency } from '@/modelos/comprobantes/comprobante.types';

interface CobranzaMedioPagoDto {
  id: string;
  numero: number;
  medioPagoId?: string;
  medioPagoNombre?: string;
  monto: number;
  numeroOperacion?: string;
  informacionBancariaId?: string;
  informacionBancariaNombre?: string;
}

interface InstallmentStateDto {
  installmentNumber: number;
  dueDate?: string;
  amountOriginal: number;
  amountPaid: number;
  remaining: number;
  status: string;
}

interface CobranzaComprobanteAplicadoDto {
  comprobanteId: string;
  serie?: string;
  correlativo?: string;
  tipoDocumentoNombre?: string;
  clienteNombre?: string;
  monto: number;
}

interface CobranzaListadoDto {
  id: string;
  serie?: string;
  correlativo?: string;
  tipoDocumentoCodigo?: string;
  tipoDocumentoNombre?: string;
  fechaCobranza: string;
  comprobanteId?: string;
  comprobanteSerie?: string;
  comprobanteCorrelativo?: string;
  clienteNombre?: string;
  clienteDocumento?: string;
  cajaId?: string;
  cajaNombre?: string;
  monedaCodigo?: string;
  totalCobrado: number;
  concepto?: string;
  estado?: string;
  createdAt: string;
  mediosPago: CobranzaMedioPagoDto[];
  installmentsInfo?: { total: number; pending: number; paid: number };
  comprobanteTipoDocumentoCodigo?: string;
  comprobanteTipoDocumentoNombre?: string;
  comprobanteFechaEmision?: string;
  comprobanteFormaPago?: string;
  clienteTipoDocumentoCodigo?: string;
  installments?: InstallmentStateDto[];
  motivoAnulacion?: string;
  comprobantesAplicados?: CobranzaComprobanteAplicadoDto[];
  usuarioNombre?: string;
}

interface EditarCobranzaInput {
  fechaCobranza?: string;
  monedaCodigo?: string;
  concepto?: string;
  mediosPago: Array<{
    numero: number;
    medioPagoId?: string;
    medioPagoNombre?: string;
    monto?: number;
    numeroOperacion?: string;
    informacionBancariaId?: string;
    informacionBancariaNombre?: string;
  }>;
}

interface CuentaPorCobrarDto {
  id: string;
  comprobanteId: string;
  comprobanteSerie?: string;
  comprobanteNumero?: string;
  tipoComprobante?: string;
  establecimientoId?: string;
  clienteNombre?: string;
  clienteDocumento?: string;
  fechaEmision: string;
  fechaVencimiento?: string;
  formaPago?: string;
  monedaCodigo?: string;
  total: number;
  cobrado: number;
  saldo: number;
  estado?: string;
  sucursal?: string;
  cajero?: string;
  totalInstallments?: number;
  pendingInstallmentsCount?: number;
  installments: InstallmentStateDto[];
}

interface CreditoPagadoApiDto {
  comprobanteId: string;
  comprobanteSerie?: string;
  comprobanteNumero?: string;
  tipoComprobante?: string;
  clienteNombre?: string;
  clienteDocumento?: string;
  fechaEmision: string;
  monedaCodigo?: string;
  total: number;
  cobrado: number;
  sucursal?: string;
  cajero?: string;
  fechaCancelacion?: string;
  totalCuotas: number;
  abonosParciales: number;
  installments: InstallmentStateDto[];
  cobranzas: CobranzaListadoDto[];
}

export interface CobranzaInputDto {
  empresaId?: string;
  establecimientoId?: string;
  comprobanteId?: string;
  comprobantesIds?: string[];
  serie?: string;
  correlativoHistorico?: string;
  tipoDocumentoCodigo?: string;
  tipoDocumentoNombre?: string;
  fechaCobranza: string;
  cajaId?: string;
  cajaNombre?: string;
  monedaCodigo?: string;
  monedaNombre?: string;
  tipoCambio?: number;
  concepto?: string;
  totalCobrado: number;
  totalRecibo?: number;
  totalVuelto?: number;
  mediosPago: Array<{
    numero: number;
    medioPagoId?: string;
    medioPagoCodigo?: string;
    medioPagoNombre?: string;
    monto: number;
    numeroOperacion?: string;
    informacionBancariaId?: string;
    informacionBancariaNombre?: string;
  }>;
  cuotaAplicaciones: Array<{
    cuotaNumero?: number | null;
    montoAplicado: number;
  }>;
}

export interface CuotaResumenCobranzaDto {
  numeroCuota: number;
  importe: number;
  montoAplicado: number;
  fechaVencimiento?: string;
}

export interface ComprobanteResumenCobranzaDto {
  comprobanteId: string;
  establecimientoId?: string;
  tipoDocumentoCodigo: string;
  serie: string;
  correlativo: string;
  estado: string;
  formaPagoNombre?: string;
  clienteNombre?: string;
  monedaCodigo: string;
  total: number;
  totalCobrado: number;
  cuotas: CuotaResumenCobranzaDto[];
}

function mapInstallment(dto: InstallmentStateDto): CobranzaInstallmentState {
  return {
    installmentNumber: dto.installmentNumber,
    dueDate: dto.dueDate,
    amountOriginal: dto.amountOriginal,
    amountPaid: dto.amountPaid,
    remaining: dto.remaining,
    status: dto.status as CobranzaInstallmentState['status'],
  };
}

function mapCuentaDto(dto: CuentaPorCobrarDto): CuentaPorCobrarSummary {
  return {
    id: dto.id,
    comprobanteId: dto.comprobanteId,
    comprobanteSerie: dto.comprobanteSerie ?? '',
    comprobanteNumero: dto.comprobanteNumero ?? '',
    tipoComprobante: dto.tipoComprobante ?? '',
    EstablecimientoId: dto.establecimientoId,
    clienteNombre: dto.clienteNombre ?? '',
    clienteDocumento: dto.clienteDocumento ?? '',
    fechaEmision: dto.fechaEmision,
    fechaVencimiento: dto.fechaVencimiento,
    formaPago: (dto.formaPago ?? 'contado') as 'contado' | 'credito',
    moneda: (dto.monedaCodigo ?? 'PEN') as Currency,
    total: dto.total,
    cobrado: dto.cobrado,
    saldo: dto.saldo,
    estado: (dto.estado ?? 'pendiente') as CuentaPorCobrarSummary['estado'],
    sucursal: dto.sucursal,
    cajero: dto.cajero,
    totalInstallments: dto.totalInstallments,
    pendingInstallmentsCount: dto.pendingInstallmentsCount,
    installments: dto.installments?.map(mapInstallment) ?? [],
    partialInstallmentsCount: dto.installments?.filter((i) => i.status === 'PARCIAL').length ?? 0,
  };
}

function mapCobranzaDto(dto: CobranzaListadoDto): CobranzaDocumento {
  const primaryMedio = dto.mediosPago?.[0];
  const medioPago = primaryMedio?.medioPagoNombre ?? '';

  const paymentLines: CobranzaPaymentLineSnapshot[] = (dto.mediosPago ?? []).map((mp) => ({
    id: mp.id,
    method: mp.medioPagoId ?? '',
    methodLabel: mp.medioPagoNombre,
    amount: mp.monto,
    operationNumber: mp.numeroOperacion,
    bankAccountLabel: mp.informacionBancariaNombre,
  }));

  const totalInstallments = dto.installmentsInfo?.total ?? 0;

  return {
    id: dto.id,
    numero: dto.serie && dto.correlativo ? `${dto.serie}-${dto.correlativo}` : dto.id,
    tipo: dto.tipoDocumentoNombre ?? 'Recibo de Cobranza',
    fechaCobranza: dto.fechaCobranza,
    createdAt: dto.createdAt,
    comprobanteId: dto.comprobanteId ?? '',
    comprobanteSerie: dto.comprobanteSerie ?? '',
    comprobanteNumero: dto.comprobanteCorrelativo ?? '',
    clienteNombre: dto.clienteNombre ?? '',
    medioPago,
    cajaDestino: dto.cajaNombre,
    moneda: (dto.monedaCodigo ?? 'PEN') as Currency,
    monto: dto.totalCobrado,
    estado: (dto.estado ?? 'activo') as CobranzaDocumento['estado'],
    motivoAnulacion: dto.motivoAnulacion,
    usuarioNombre: dto.usuarioNombre,
    installmentsInfo: totalInstallments > 0 ? dto.installmentsInfo : undefined,
    paymentLines: paymentLines.length > 0 ? paymentLines : undefined,
    comprobantesAplicados: dto.comprobantesAplicados?.length
      ? dto.comprobantesAplicados.map((c) => ({
          comprobanteId: c.comprobanteId,
          serie: c.serie ?? '',
          correlativo: c.correlativo ?? '',
          tipoDocumentoNombre: c.tipoDocumentoNombre ?? '',
          clienteNombre: c.clienteNombre ?? '',
          monto: c.monto,
        }))
      : undefined,
  };
}

function localISOString(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function normalizeFechaCobranza(value?: string): string {
  const match = value?.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}:\d{2}))?/);
  if (!match) return localISOString();
  if (match[2]) return `${match[1]}T${match[2]}`;
  const now = localISOString();
  return `${match[1]}T${now.split('T')[1]}`;
}

const MONEDA_NOMBRES: Record<string, string> = {
  PEN: 'Soles',
  USD: 'Dólares',
  EUR: 'Euros',
};

function buildInputDto(input: RegistrarCobranzaInput): CobranzaInputDto {
  const { cuenta, payload } = input;
  const lines = payload.lines ?? [];
  const totalCobrado = lines.reduce((sum, l) => sum + l.amount, 0);

  const mediosPago = lines.map((line, index) => ({
    numero: index + 1,
    medioPagoId: line.method,
    medioPagoCodigo: line.methodCode,
    medioPagoNombre: line.methodLabel,
    monto: line.amount,
    numeroOperacion: line.operationNumber,
    informacionBancariaNombre: line.bank,
  }));

  const cuotaAplicaciones = payload.allocations?.length
    ? payload.allocations.map((a) => ({
        cuotaNumero: a.installmentNumber,
        montoAplicado: a.amount,
      }))
    : [{ cuotaNumero: null, montoAplicado: totalCobrado }];

  return {
    comprobanteId: cuenta.comprobanteId,
    establecimientoId: cuenta.EstablecimientoId,
    serie: payload.collectionDocument?.seriesCode,
    tipoDocumentoCodigo: 'RCB',
    tipoDocumentoNombre: 'Recibo de Cobranza',
    fechaCobranza: normalizeFechaCobranza(payload.fechaCobranza),
    cajaId: payload.cajaDestinoId,
    cajaNombre: payload.cajaDestino ?? payload.cajaDestinoLabel,
    monedaCodigo: payload.collectionCurrency,
    monedaNombre: payload.collectionCurrency ? (MONEDA_NOMBRES[payload.collectionCurrency] ?? payload.collectionCurrency) : undefined,
    tipoCambio: payload.collectionExchangeRate,
    concepto: payload.notes,
    totalCobrado,
    mediosPago,
    cuotaAplicaciones,
  };
}

function buildInputDtoMultiple(input: RegistrarCobranzaMultipleInput): CobranzaInputDto {
  const { cuentas, payload } = input;
  const lines = payload.lines ?? [];
  const totalCobrado = lines.reduce((sum, l) => sum + l.amount, 0);
  const primera = cuentas[0];

  const mediosPago = lines.map((line, index) => ({
    numero: index + 1,
    medioPagoId: line.method,
    medioPagoCodigo: line.methodCode,
    medioPagoNombre: line.methodLabel,
    monto: line.amount,
    numeroOperacion: line.operationNumber,
    informacionBancariaNombre: line.bank,
  }));

  return {
    comprobantesIds: cuentas.map((c) => c.comprobanteId),
    establecimientoId: primera?.EstablecimientoId,
    serie: payload.collectionDocument?.seriesCode,
    tipoDocumentoCodigo: 'RCB',
    tipoDocumentoNombre: 'Recibo de Cobranza',
    fechaCobranza: normalizeFechaCobranza(payload.fechaCobranza),
    cajaId: payload.cajaDestinoId,
    cajaNombre: payload.cajaDestino ?? payload.cajaDestinoLabel,
    monedaCodigo: primera?.moneda,
    monedaNombre: primera?.moneda ? (MONEDA_NOMBRES[primera.moneda] ?? primera.moneda) : undefined,
    tipoCambio: 1,
    concepto: payload.notes,
    totalCobrado,
    mediosPago,
    cuotaAplicaciones: [],
  };
}

function mapCreditoPagadoDto(dto: CreditoPagadoApiDto): CreditoPagadoResumen {
  const cuenta: CuentaPorCobrarSummary = {
    id: dto.comprobanteId,
    comprobanteId: dto.comprobanteId,
    comprobanteSerie: dto.comprobanteSerie ?? '',
    comprobanteNumero: dto.comprobanteNumero ?? '',
    tipoComprobante: dto.tipoComprobante ?? '',
    clienteNombre: dto.clienteNombre ?? '',
    clienteDocumento: dto.clienteDocumento ?? '',
    fechaEmision: dto.fechaEmision,
    formaPago: 'credito',
    moneda: (dto.monedaCodigo ?? 'PEN') as import('@/modelos/comprobantes/comprobante.types').Currency,
    total: dto.total,
    cobrado: dto.cobrado,
    saldo: 0,
    estado: 'cancelado',
    sucursal: dto.sucursal,
    cajero: dto.cajero,
    totalInstallments: dto.totalCuotas || undefined,
    pendingInstallmentsCount: 0,
    installments: dto.installments?.map(mapInstallment) ?? [],
  };
  const cobranzas = dto.cobranzas.map(mapCobranzaDto);
  const totalCuotas = dto.totalCuotas;
  return {
    cuenta,
    installments: cuenta.installments ?? [],
    cuotasLabel: totalCuotas ? `${totalCuotas} de ${totalCuotas}` : '—',
    totalCuotas,
    cancelacion: dto.fechaCancelacion,
    cobranzas,
    cobrosCount: cobranzas.length,
    abonosParciales: dto.abonosParciales,
  };
}

export const cobranzasService = {
  getCuentas: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    estado?: string;
    cajeroId?: string;
    formaPago?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<CuentaPorCobrarDto[]>> =>
    httpClient.get('/cobranzas/cuentas', params as Record<string, string | number | undefined>),

  getAll: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    formaPagoId?: string;
    medioPagoId?: string;
    cajeroId?: string;
    comprobanteId?: string;
  }): Promise<ApiResponse<CobranzaListadoDto[]>> =>
    httpClient.get('/cobranzas', params as Record<string, string | number | undefined>),

  getById: (id: string): Promise<ApiResponse<CobranzaListadoDto>> =>
    httpClient.get(`/cobranzas/${id}`),

  create: (data: CobranzaInputDto): Promise<ApiResponse<CobranzaListadoDto>> =>
    httpClient.post('/cobranzas', data),

  anular: (id: string, motivo: string): Promise<ApiResponse<unknown>> =>
    httpClient.patch(`/cobranzas/${id}/anular`, { motivo }),

  update: (id: string, data: EditarCobranzaInput): Promise<ApiResponse<unknown>> =>
    httpClient.put(`/cobranzas/${id}`, data),

  buscarResumenComprobantes: (empresaId: string, claves: string[]): Promise<ApiResponse<ComprobanteResumenCobranzaDto[]>> =>
    httpClient.post('/cobranzas/resumen-comprobantes', { claves }, { empresaId }),

  getCreditosPagados: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    medioPagoId?: string;
    cajeroId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<CreditoPagadoApiDto[]>> =>
    httpClient.get('/cobranzas/creditos-pagados', params as Record<string, string | number | undefined>),

  mapCuentaDto,
  mapCobranzaDto,
  mapCreditoPagadoDto,
  buildInputDto,
  buildInputDtoMultiple,
};
